import { useState } from 'react';
import { SharedCard } from '../../../../../components/shared-card/card';
import { Box, Button, capitalize, Grid2, Typography, useMediaQuery, useTheme } from '@mui/material';
import { loanTypeMap, viewApplicationStatusCodes, showContinueJourneyStatusCodes } from '../utils';
import { LoanDetailDrawer } from './LoanDetailDrawer';
import { formatDate } from '../utils';

import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Check from '@mui/icons-material/Check';

import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';

import { Icon } from '@mui/material';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LowPriorityIcon from '@mui/icons-material/LowPriority';

import { QontoConnector, QontoStepIconRoot } from './QontoConnector';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../services/auth/AuthContext';
import { Close } from '@mui/icons-material';
import { CUSTOMER_FORM_URL, optimo_whatsapp_number } from '../../../../../config';

const leadStatusLabels = {
    "InprogressForTwoMonths - ConsideredRejected": "dashboard.application.lead_status.inprogress_for_two_months_pending",
    "Disbursed": "dashboard.application.lead_status.disbursed",
    "Inprogress": "dashboard.application.lead_status.inprogress",
    "CreditBureauRejected": "dashboard.application.lead_status.credit_bureau_rejected",
    "FIRejected": "dashboard.application.lead_status.fi_rejected",
    "NegotiationRejected": "dashboard.application.lead_status.negotiation_rejected",
    "CRIFAutoRejected": "dashboard.application.lead_status.crif_auto_rejected",
    "FieldRejectNotUpdatedInSystem": "dashboard.application.lead_status.field_reject_not_updated_in_system",
    "CAMRejected": "dashboard.application.lead_status.cam_rejected",
    "MoRepaymentRejected": "dashboard.application.lead_status.mo_repayment_rejected",
    "BizDocRejected": "dashboard.application.lead_status.biz_doc_rejected",
    "BankStatementRejected": "dashboard.application.lead_status.bank_statement_rejected",
    "KFSRejected": "dashboard.application.lead_status.kfs_rejected",
    "BankDetailsRejected": "dashboard.application.lead_status.bank_details_rejected",
    "DocUploadRejected": "dashboard.application.lead_status.doc_upload_rejected",
    "DocVerificationRejected": "dashboard.application.lead_status.doc_verification_rejected"
};

const leadCurrentStageLabels = {
	'Lead Approved & CAM Decision (UBL) or Valuation/Legal (LAP) Pending': 'dashboard.application.lead_current_stage.cam_valuation_pending',
	'Customer Created & Lead Creation Pending': 'dashboard.application.lead_current_stage.lead_creation_pending',
	'Bank Statement Upload Pending Done & Lead Approval Pending': 'dashboard.application.lead_current_stage.lead_approval_pending',
	'Lead Created & Credit Bureau Approval Pending': 'dashboard.application.lead_current_stage.credit_report_pending',
	'Valuation and Legal Done & CAM Decision Pending': 'dashboard.application.lead_current_stage.cam_decision_pending',
	'Biz Doc Upload Done & Bank Details Update Pending': 'dashboard.application.lead_current_stage.bank_details_pending',
	'Doc Upload Done & Doc Verification Pending': 'dashboard.application.lead_current_stage.doc_verification_pending',
	'CAM Approved & Negotiation Pending': 'dashboard.application.lead_current_stage.negotiation_pending',
	'KFS Done & Doc Upload Pending': 'dashboard.application.lead_current_stage.doc_upload_pending',
	'Esign done & Disbursement Pending': 'dashboard.application.lead_current_stage.disbursement_pending',
	'FI Completion done & Biz Doc Upload Pending': 'dashboard.application.lead_current_stage.biz_doc_pending',
	'Doc Verification Done & Mode of Repayment Pending': 'dashboard.application.lead_current_stage.mode_of_repayment_pending',
	'Final Approval done & Esign Pending': 'dashboard.application.lead_current_stage.esign_pending',
	'Negotiation Done & KFS Pending': 'dashboard.application.lead_current_stage.kfs_pending',
};

export default function LoanCard({
	title,
	application,
	addNewChatRecord,
	allStatus,
	currentStatusCode,
	resellerData,
	rejectApplication,
	loanType='',
	showStepper = true,
}) {
	const theme = useTheme();
	const isMdDownScreen = useMediaQuery(theme.breakpoints.down("md"));

	const { t, i18n } = useTranslation();
	const { user } = useAuth();
	const userRoles = user.role;
	// An account can hold borrower alongside a working role - a partner who is
	// also a borrower, for instance. The borrower-only view is a downgrade, so
	// it applies only when borrower is all they are.
	const isBorrower = userRoles.includes('borrower') &&
		!['support', 'partner', 'connector', 'rm', 'bc_partner', 'admin']
			.some((role) => userRoles.includes(role));
	const isSupport = userRoles.includes('support');
	const isPartner = userRoles.includes('partner');
	const isRM = userRoles.includes('rm');
	const applicationSubmissionStatus = application.enquiry_details.application_submission_status;
	const isRejected = ['rejected'].includes(applicationSubmissionStatus);
	const isCancelled = ['cancelled'].includes(applicationSubmissionStatus);
	const isUnserviceable = ['unserviceable'].includes(applicationSubmissionStatus);
	const isCancelledOrUnserviceable = isCancelled || isUnserviceable;
	const [priority, setPriority] = useState(application.enquiry_details.priority)

	const data = {
		'dashboard.application.applicant_name': `${application.enquiry_details.first_name ?? '-'} ${application.enquiry_details.last_name ?? '-'}`,
		// 'dashboard.application.loan_type': loanTypeValueToLabel[application.loan_type],
		'dashboard.application.loan_type': application.loan_type?.name,
		'dashboard.application.expected_loan_amount': application.enquiry_details?.required_loan_amount,
		'dashboard.application.expected_emi_amount': application.enquiry_details?.comfortable_emi_amount,
	};

	if (application.current_status_code === "OPTLAP10") {
		const inprinciple = application.inprinciple_detail;
		data['dashboard.application.inprinciple_loan_amount'] = inprinciple.sanction_loan_amount;
		data['dashboard.application.inprinciple_emi'] = inprinciple.sanction_emi_amount;
		data['dashboard.application.tenure'] = inprinciple.sanction_tenure;
		data['dashboard.application.business_pincode'] = application.enquiry_details.business_pincode;
	}
	const [open, setOpen] = useState(false);

	function QontoStepIcon(props) {
		const { active, completed, className } = props;

		return (
			<QontoStepIconRoot ownerState={{ active }} className={className}>
				{completed ? (
					<Check className="QontoStepIcon-completedIcon" />
				) : (
					<div className="QontoStepIcon-circle" />
				)}
			</QontoStepIconRoot>
		);
	}

	QontoStepIcon.propTypes = {
		/**
		 * Whether this step is active.
		 * @default false
		 */
		active: PropTypes.bool,
		className: PropTypes.string,
		/**
		 * Mark the step as completed. Is passed to child components.
		 * @default false
		 */
		completed: PropTypes.bool,
	};

	const handleShare = (event) => {
		event.stopPropagation();
		const preferred_language = application.enquiry_details.preferred_language || 'en';
		const template = allStatus[currentStatusCode].reminder[preferred_language];
		const first_name = application.enquiry_details.first_name || '';
		const loan_type = application.loan_type.name;
		const message = template.replace("{}", first_name).replace("{}", loan_type).replace("{optimo_whatsapp_number}", optimo_whatsapp_number);
		const whatsapp_number = application.enquiry_details.whatsapp_number;
		let url = `https://wa.me/${whatsapp_number}?text=${encodeURIComponent(message)}`;
		window.open(url, '_blank');
	};

	const showViewApplicationButton = isSupport &&
		viewApplicationStatusCodes.includes(application.current_status_code) && applicationSubmissionStatus !== 'rejected' &&
		!application.current_status_code.startsWith(`OPT${loanType}01`);
	const showContinueJourneyButton = (isSupport || isPartner) && 
		showContinueJourneyStatusCodes.includes(application.current_status_code) && 
		applicationSubmissionStatus !== 'rejected' &&
		!application.current_status_code.startsWith(`OPT${loanType}01`);
	const handleViewApplication = (event) => {
		event.stopPropagation();
		const enquiryId = application.enquiry_details.id;
		window.open(`${CUSTOMER_FORM_URL}/admin/submissions/${enquiryId}`);
	};
	const handleContinueJourney = (event) => {
		event.stopPropagation();
		const flowToken = application.enquiry_details.flow_token;
		window.open(`${CUSTOMER_FORM_URL}/verification/${flowToken}`);
	};

	const action = {
		'none': {
			'icon': <PriorityHighIcon />,
			'color': 'blue'
		},
		'low': {
			'icon': <LowPriorityIcon />,
			'color': 'grey'
		},
		'medium': {
			'icon': <KeyboardArrowUpIcon />,
			'color': 'orange'
		},
		'high': {
			'icon': <KeyboardDoubleArrowUpIcon />,
			'color': 'green'
		},
		'not_interested': {
			'icon': <NotInterestedIcon />,
			'color': 'red'
		}
	}

	const renderTags = (position) => {
		const isPositionTop = position === 'top';
		const sentToField = application.enquiry_details.application_submission_status === 'pushed_to_field';
		const Tag = ({ translate, background, children, width='220px' }) => (
			<Typography
				sx={{
					...(isPositionTop && {
						position: 'absolute',
						top: 0,
						right: 0,
						translate,
						width: width,
						textAlign: 'center',
					}),
					background: background || '#999999',
					color: 'white',
					fontSize: '0.875rem',
					fontWeight: 'bold',
					padding: '0.15rem 0.75rem',
					boxShadow: '2px 2px 3px 1px rgba(0, 0, 0, 0.5)'
				}}
			>
				{children || 'N/A'}
			</Typography>
		);

		return (
			<>
				{(isSupport || isPartner || isRM ) && (
					<Tag
						background={'#5d6d7e'}
						translate={'-490px -5px'}
					>
						{resellerData[application.enquiry_details.reseller_referral_code]?.first_name} {resellerData[application.enquiry_details.reseller_referral_code]?.last_name || "N/A"}  
					</Tag>
				)}

				<Tag
					background={(isCancelledOrUnserviceable || isRejected)
						? 'crimson'
						: (allStatus[currentStatusCode]?.color ? theme.palette[allStatus[currentStatusCode].color]?.main : '#999999')
					}
					translate={'-265px -5px'}
				>
					<Box
						display={'flex'}
						justifyContent={'center'}
						alignItems={'center'}
						gap={1}
					>
						{isCancelledOrUnserviceable || isRejected ? (
							<>
								<Close fontSize="inherit" />
								{capitalize(applicationSubmissionStatus)}
							</>
						) : sentToField ? (
							t(leadStatusLabels[application.enquiry_details.lead_data.status]) || 'N/A'
						) : (
							allStatus[currentStatusCode]?.label?.[i18n.language] || 'N/A'
						)}
					</Box>
				</Tag>

				<Tag
					background={'#999999'}
					translate={'-40px -5px'}
				>
					{isRejected
						? application.enquiry_details.rejection_reason
						: isUnserviceable
							? 'Out of Geolocation'
								: isCancelled
									? 'User Restarted'
									: sentToField
										? t(leadCurrentStageLabels[application.enquiry_details.lead_data.current_stage])
										: t(loanTypeMap[application.loan_type?.code]?.label || '')
					}
				</Tag>

				{
					(isSupport || isRM) && !(isCancelledOrUnserviceable || isRejected) && priority && action[priority] && (
						<Box sx={{'position': 'absolute', left: 5, top: -5}}>
							<Icon sx={{'background': action[priority]?.color || '#999999', width: '30px', 'height': '35px', 'color': '#fff'}}>
								{action[priority]?.icon || <PriorityHighIcon />}
							</Icon>
						</Box>
					)
				}
			</>
		);
	};

	return (
		<>
			<LoanDetailDrawer
				open={open}
				handleClose={() => setOpen(false)}
				initialData={application}
				propTypes={priority}
				priority={priority}
				setPriority={setPriority}
				showSupportChat={
					!['unserviceable', 'cancelled', 'rejected'].includes(
						application.enquiry_details.application_submission_status
					)
				}
				addNewChatRecord={addNewChatRecord}
				rejectApplication={rejectApplication}
			/>
			<SharedCard
				sx={{
					position: 'relative',
					width: '100% !important',
					py: 3,
					cursor: 'pointer',
					gap: 1,
					overflow: 'visible',
					boxShadow: '0 0 14px 4px #3333',
				}}
				onClick={() => setOpen(true)}
			>
				{!isMdDownScreen && renderTags('top')}

				<Typography fontSize="large" fontWeight="bold" textAlign={'left'}>
					{title}
				</Typography>

				<Grid2 container xs={12} spacing={2} alignContent="stretch">
					{Object.entries(data).map(([label, value], i) => (
						<Grid2 key={i} size={{ xs: 6, sm: 3 }}>
							<Box
								p="0.25rem"
								border="1px solid"
								borderColor="divider"
								borderRadius="0.25rem"
								height="100%"
							>
								<Typography fontWeight="bold" mb={0.5}>
									{t(label)}
								</Typography>
								<Typography>{t(value ?? '-')}</Typography>
							</Box>
						</Grid2>
					))}
				</Grid2>
				<Grid2 container spacing={2} mt={2}>
					{/* Stepper Section */}
					{showStepper && (
						<Grid2 size={{ xs: 12, sm: (isBorrower || showViewApplicationButton || showContinueJourneyButton) ? 9 : 12 }} overflow={'auto'}>
							<Stack spacing={4}>
								<Stepper
									alternativeLabel
									activeStep={allStatus[currentStatusCode]?.order - 1 || 0}
									connector={<QontoConnector />}
								>
									{Object.keys(allStatus).length > 0 ? (
										Object.keys(allStatus).map((key) => (
											<Step key={key}>
												<StepLabel StepIconComponent={QontoStepIcon}>
													{allStatus[key].header[i18n.language]}
												</StepLabel>
											</Step>
										))
									) : (
										<Typography>No steps available</Typography>
									)}
								</Stepper>
							</Stack>
						</Grid2>
					)}

					{/* Button Section. ml:auto keeps these at the right edge in the
					    sections that hide the stepper, where there is no column
					    to their left to push them over. */}
					{
						isBorrower &&
						<Grid2 size={{ xs: 12, sm: 3 }} alignContent={'center'} sx={{ ml: 'auto' }}>
							<Button
								sx={(theme) => ({
									backgroundColor: theme.palette.primary.main,
									color: '#fff',
									'&:hover': { color: '#fff' },
								})}
								onClick={handleShare}
								fullWidth>
								{t('dashboard.send_reminder')}
							</Button>
						</Grid2>
					}

					{showViewApplicationButton && (
						<Grid2 size={{ xs: 12, sm: 3 }} alignContent={'center'} sx={{ ml: 'auto' }}>
							<Button
								sx={(theme) => ({
									backgroundColor: theme.palette.primary.main,
									color: '#fff',
									'&:hover': { color: '#fff' },
								})}
								onClick={handleViewApplication}
								fullWidth
							>
								{t('View Application')}
							</Button>
						</Grid2>
					)}

					{showContinueJourneyButton && (
						<Grid2 size={{ xs: 12, sm: 3 }} alignContent={'center'} sx={{ ml: 'auto' }}>
							<Button
								sx={(theme) => ({
									backgroundColor: theme.palette.primary.main,
									color: '#fff',
									'&:hover': { color: '#fff' },
								})}
								onClick={handleContinueJourney}
								fullWidth
							>
								{t('Continue Journey')}
							</Button>
						</Grid2>
					)}

					{/* Timestamp Section */}
					<Box
						display="flex"
						justifyContent="space-between"
						width="100%"
					>
						<Typography>
							<b>Created At:</b> {formatDate(application.enquiry_details.created_at)}
						</Typography>
						{
							isSupport && (
								<Typography>
									<b>Last Call At:</b> {formatDate(application.enquiry_details.last_call_time)}
								</Typography>
							)
						}
						<Typography>
							<b>Last Activity:</b> {formatDate(
								application.enquiry_details.last_activity_at
								?? application.enquiry_details.updated_at
							)}
						</Typography>
					</Box>
				</Grid2>

				{isMdDownScreen && (
					<Box
						display={'flex'}
						justifyContent={'center'}
						flexWrap={'wrap'}
						gap={1}
					>
						{renderTags('bottom')}
					</Box>
				)}
			</SharedCard>
		</>
	);
}
